import React, { useCallback, useRef } from 'react';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';

// import Tinode from 'tinode-sdk';
//const Drafty = Tinode.Drafty;
import { Tinode } from 'tinode-sdk';
import {Drafty} from "tinode-sdk";

import ChatMessage from '../widgets/chat-message.jsx';
import DocPreview from '../widgets/doc-preview.jsx';
import ImagePreview from '../widgets/image-preview.jsx';
import Invitation from '../widgets/invitation.jsx';
import LoadSpinner from '../widgets/load-spinner.jsx';
import LogoView from './logo-view.jsx';
import SendMessage from '../widgets/send-message.jsx';

import { DEFAULT_P2P_ACCESS_MODE, IMAGE_PREVIEW_DIM, KEYPRESS_DELAY, MESSAGES_PAGE,
  MAX_EXTERN_ATTACHMENT_SIZE, MAX_IMAGE_DIM, MAX_INBAND_ATTACHMENT_SIZE, READ_DELAY } from '../config.js';
import { SUPPORTED_IMAGE_FORMATS, blobToBase64, filePasted, fileToBase64,
  imageScaled, makeImageDataUrl } from '../lib/blob-helpers.js';
import HashNavigation from '../lib/navigation.js';
import { bytesToHumanSize, shortDateFormat } from '../lib/strformat.js';
import { BiArrowFromLeft }  from "react-icons/bi";

// Run timer with this frequency (ms) for checking notification queue.
const NOTIFICATION_EXEC_INTERVAL = 300;

const messages = defineMessages({
  online_now: {
    id: 'online_now',
    defaultMessage: 'online now',
    description: 'Indicator that the user or topic is currently online',
  },
  last_seen: {
    id: 'last_seen_timestamp',
    defaultMessage: 'Last seen',
    description: 'Label for the timestamp of when the user or topic was last online'
  },
  not_found: {
    id: 'title_not_found',
    defaultMessage: 'Not found',
    description: 'Title shown when topic is not found'
  },
  channel: {
    id: 'channel',
    defaultMessage: 'channel',
    description: 'Subtitle shown for channels in MessagesView instead of last seen'
  },
  file_attachment_too_large: {
    id: 'file_attachment_too_large',
    defaultMessage: 'The file size {size} exceeds the {limit} limit.',
    description: 'Error message when attachment is too large'
  }
});

// Checks if the access permissions are granted but not yet accepted.
function isUnconfirmed(acs) {
  if (acs) {
    const ex = acs.getExcessive() || '';
    return acs.isJoiner('given') && (ex.includes('R') || ex.includes('W'));
  }
  return false;
}

function isPeerRestricted(acs) {
  if (acs) {
    const ms = acs.getMissing() || '';
    return acs.isJoiner('want') && (ms.includes('R') || ms.includes('W'));
  }
  return false;
}

function isStreamGroupTopic(topicName) {
  return topicName && topicName.startsWith('grpSt');
}

function isAnonymousStreamUser(userId) {
  return userId && userId.startsWith('anoSt');
}

// Stream group ACS is often incomplete until subscribe finishes.
function acsFromMeta(acs, topic) {
  if (!acs) {
    return null;
  }
  const isReader = acs.isReader();
  const readingBlocked = !acs.isReader('given');
  if (topic && isStreamGroupTopic(topic.name) && !topic.isSubscribed()) {
    return {isWriter: acs.isWriter(), isReader: true, readingBlocked: false, unconfirmed: isUnconfirmed(acs)};
  }
  return {isWriter: acs.isWriter(), isReader, readingBlocked, unconfirmed: isUnconfirmed(acs)};
}

// Parent topicSelectedAcs is often null before me-subscribe completes; fall back to topic ACS.
function resolveTopicAcs(props) {
  if (props.acs) {
    return props.acs;
  }
  if (props.tinode && props.topic) {
    const topic = props.tinode.getTopic(props.topic);
    if (topic) {
      return topic.getAccessMode();
    }
  }
  return null;
}

class MessagesView extends React.Component {
  constructor(props) {
    super(props);

    this.state = MessagesView.getDerivedStateFromProps(props, {});

    this.leave = this.leave.bind(this);
    this.sendImageAttachment = this.sendImageAttachment.bind(this);
    this.sendFileAttachment = this.sendFileAttachment.bind(this);
    this.sendKeyPress = this.sendKeyPress.bind(this);
    this.handleScrollReference = this.handleScrollReference.bind(this);
    this.handleScrollEvent = this.handleScrollEvent.bind(this);
    this.handleDescChange = this.handleDescChange.bind(this);
    this.handleSubsUpdated = this.handleSubsUpdated.bind(this);
    this.handleMessageUpdate = this.handleMessageUpdate.bind(this);
    this.handleAllMessagesReceived = this.handleAllMessagesReceived.bind(this);
    this.handleInfoReceipt = this.handleInfoReceipt.bind(this);
    this.handleImagePostview = this.handleImagePostview.bind(this);
    this.handleClosePreview = this.handleClosePreview.bind(this);
    this.handleFormResponse = this.handleFormResponse.bind(this);
    this.handleContextClick = this.handleContextClick.bind(this);
    this.handleShowContextMenuMessage = this.handleShowContextMenuMessage.bind(this);
    this.handleNewChatAcceptance = this.handleNewChatAcceptance.bind(this);
    this.handleEnablePeer = this.handleEnablePeer.bind(this);
    this.handleCancelUpload = this.handleCancelUpload.bind(this);
    this.postReadNotification = this.postReadNotification.bind(this);
    this.clearNotificationQueue = this.clearNotificationQueue.bind(this);
    this.loadedSubscription = this.loadedSubscription.bind(this);
    this.retrySubscription = this.retrySubscription.bind(this);

    this.readNotificationQueue = [];
    this.readNotificationTimer = null;
  }

  componentDidMount() {
    // this.propsChange(this.props, this.state);
    if (this.messagesScroller) {
      this.messagesScroller.addEventListener('scroll', this.handleScrollEvent);
    }
  }

  componentWillUnmount() {
    if (this.messagesScroller) {
      this.messagesScroller.removeEventListener('scroll', this.handleScrollEvent);
    }

    // Flush all notifications.
    this.clearNotificationQueue();
  }

  // Scroll last message into view on component update e.g. on message received
  // or vertical shrinking.
  componentDidUpdate(prevProps, prevState) {
    if (this.messagesScroller) {
      if (prevState.topic != this.state.topic || prevState.messageCount != this.state.messageCount) {
        // New message
        this.messagesScroller.scrollTop = this.messagesScroller.scrollHeight - this.state.scrollPosition;
      } else if (prevProps.viewportHeight > this.props.viewportHeight) {
        // Componet changed height.
        this.messagesScroller.scrollTop += prevProps.viewportHeight - this.props.viewportHeight;
      }
    }

    const topic = this.props.tinode ? this.props.tinode.getTopic(this.state.topic) : undefined;
    if (this.state.topic != prevState.topic) {
      if (prevState.topic && !Tinode.isNewGroupTopicName(prevState.topic)) {
        this.leave(prevState.topic);
      }

      if (topic) {
        topic.onData = this.handleMessageUpdate;
        topic.onAllMessagesReceived = this.handleAllMessagesReceived;
        topic.onInfo = this.handleInfoReceipt;
        topic.onMetaDesc = this.handleDescChange;
        topic.onSubsUpdated = this.handleSubsUpdated;
        topic.onPres = this.handleSubsUpdated;
      }
    }

    if (!this.props.applicationVisible) {
      // If application is not visible, flush all unsent 'read' notifications.
      this.clearNotificationQueue();
    } else {
      // Otherwise assume there are unread messages.
      this.postReadNotification(0);
    }

    if (topic && !topic.isSubscribed() && this.props.ready &&
        ((this.state.topic != prevState.topic) || !prevProps.ready)) {
      // Is this a new topic?
      const newTopic = (this.props.newTopicParams && this.props.newTopicParams._topicName == this.props.topic);

      // Don't request the tags. They are useless unless the user
      // is the owner and is editing the topic.
      let getQuery = topic.startMetaQuery().withLaterDesc().withLaterSub();
      const isStreamGroup = isStreamGroupTopic(this.state.topic);
      if (this.state.isReader || newTopic || isStreamGroup) {
        // Reading is either permitted or we don't know because it's a new topic. Ask for messages.
        getQuery = getQuery.withLaterData(MESSAGES_PAGE);
        if (this.state.isReader || isStreamGroup) {
          getQuery = getQuery.withLaterDel();
        }
        // And show "loading" spinner.
        this.setState({ fetchingMessages: true });
      }
      const setQuery = newTopic ? this.props.newTopicParams : undefined;
      this.retrySubscription(topic, getQuery, setQuery);
    }
  }

  retrySubscription(topic, getQuery, setQuery, tryNumber=0) {
    topic.subscribe(getQuery.build(), setQuery)
        .then((ctrl) => {
          this.loadedSubscription(ctrl);
        })
        .catch((err) => {
          if (tryNumber < 3 && topic && topic.name.startsWith('grpSt')) {
            setTimeout(() => {
              this.retrySubscription(topic, getQuery, setQuery, tryNumber + 1);
            }, 5000);
          } else {
            console.log("Failed subscription to", this.state.topic);
            this.props.onError(err.message, 'err');
            const blankState = MessagesView.getDerivedStateFromProps({}, {});
            blankState.title = this.props.intl.formatMessage(messages.not_found);
            this.setState(blankState);
          }
        });
  }

  loadedSubscription(ctrl) {
    if (ctrl.code == 303) {
      // Redirect to another topic requested.
      HashNavigation.navigateTo(HashNavigation.setUrlTopic('', ctrl.params.topic));
      return;
    }
    if (this.state.topic != ctrl.topic) {
      this.setState({topic: ctrl.topic});
    }
    const topic = this.props.tinode.getTopic(this.state.topic);
    if (topic) {
      const acs = topic.getAccessMode();
      const synced = acsFromMeta(acs, topic);
      if (synced) {
        this.setState(synced);
        if (this.props.onTopicAcsUpdate) {
          this.props.onTopicAcsUpdate(acs);
        }
      }
    }
    this.props.onNewTopicCreated(this.props.topic, ctrl.topic);
    // If there are unsent messages, try sending them now.
    topic.queuedMessages((pub) => {
      if (!pub._sending && topic.isSubscribed()) {
        topic.publishMessage(pub);
      }
    });
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    let nextState = {};
    if (!nextProps.topic) {
      // Default state: no topic.
      nextState = {
        messageCount: 0,
        latestClearId: -1,
        onlineSubs: [],
        topic: null,
        title: '',
        avatar: null,
        docPreview: null,
        imagePreview: null,
        imagePostview: null,
        typingIndicator: false,
        scrollPosition: 0,
        fetchingMessages: false,
        peerMessagingDisabled: false,
        channel: false,
        isReader: true,
        scrolling: false,
      };
    } else if (nextProps.topic != prevState.topic) {
      const topic = nextProps.tinode.getTopic(nextProps.topic);
      nextState = {
        topic: nextProps.topic,
        docPreview: null,
        imagePreview: null,
        imagePostview: null,
        typingIndicator: false,
        scrollPosition: 0,
        fetchingMessages: false,
        isReader: true,
        scrolling: false,
      };

      if (topic) {
        // Topic exists.
        const subs = [];

        if (nextProps.connected) {
          topic.subscribers((sub) => {
            if (sub.online && sub.user != nextProps.myUserId) {
              subs.push(sub);
            }
          });
        }

        Object.assign(nextState, {
          onlineSubs: subs
        });

        if (topic.public) {
          Object.assign(nextState, {
            title: topic.public.fn,
            avatar: makeImageDataUrl(topic.public.photo)
          });
        } else {
          Object.assign(nextState, {
            title: '',
            avatar: null
          });
        }

        const peer = topic.p2pPeerDesc();
        if (peer) {
          Object.assign(nextState, {
            peerMessagingDisabled: isPeerRestricted(peer.acs)
          });
        } else if (prevState.peerMessagingDisabled) {
          Object.assign(nextState, {
            peerMessagingDisabled: false
          });
        }
        Object.assign(nextState, {
          messageCount: topic.messageCount(),
          latestClearId: topic.maxClearId(),
          channel: topic.isChannelType()
        });

        const topicAcs = topic.getAccessMode();
        const synced = acsFromMeta(topicAcs, topic);
        if (synced) {
          Object.assign(nextState, synced);
        }
      } else {
        // Invalid topic.
        Object.assign(nextState, {
          messageCount: 0,
          latestClearId: -1,
          onlineSubs: [],
          title: '',
          avatar: null,
          peerMessagingDisabled: false,
          channel: false
        });
      }
    }

    const acs = resolveTopicAcs(nextProps);
    const topic = nextProps.tinode && nextProps.topic ?
      nextProps.tinode.getTopic(nextProps.topic) : null;
    if (acs) {
      const synced = acsFromMeta(acs, topic);
      if (synced.isWriter !== prevState.isWriter) {
        nextState.isWriter = synced.isWriter;
      }
      if (synced.isReader !== prevState.isReader) {
        nextState.isReader = synced.isReader;
      }
      if (synced.readingBlocked !== prevState.readingBlocked) {
        nextState.readingBlocked = synced.readingBlocked;
      }
      if (acs.isSharer() !== prevState.isSharer) {
        nextState.isSharer = acs.isSharer();
      }
      if (synced.unconfirmed !== prevState.unconfirmed) {
        nextState.unconfirmed = synced.unconfirmed;
      }
    } else if (!nextProps.connected) {
      if (prevState.isWriter) {
        nextState.isWriter = false;
      }
      if (!prevState.readingBlocked) {
        nextState.readingBlocked = true;
      }
      if (prevState.isSharer) {
        nextState.isSharer = false;
      }
    }

    // Clear subscribers online when there is no connection.
    if (!nextProps.connected && prevState.onlineSubs && prevState.onlineSubs.length > 0) {
      nextState.onlineSubs = [];
    }

    return nextState;
  }

  leave(oldTopicName) {
    if (!oldTopicName || !this.props.tinode.isTopicCached(oldTopicName)) {
      return;
    }
    const oldTopic = this.props.tinode.getTopic(oldTopicName);
    if (oldTopic && oldTopic.isSubscribed()) {
      oldTopic.leave(false)
        .catch(() => { /* do nothing here */ })
        .finally(() => {
          // We don't care if the request succeeded or failed.
          // The topic is dead regardless.
          this.setState({fetchingMessages: false});
          oldTopic.onData = undefined;
          oldTopic.onAllMessagesReceived = undefined;
          oldTopic.onInfo = undefined;
          oldTopic.onMetaDesc = undefined;
          oldTopic.onSubsUpdated = undefined;
          oldTopic.onPres = undefined;
        });
    }
  }

  handleScrollReference(node) {
    if (node) {
      node.addEventListener('scroll', this.handleScrollEvent);
      this.messagesScroller = node;
      this.messagesScroller.scrollTop = this.messagesScroller.scrollHeight - this.state.scrollPosition;
    }
  }

  // Get older messages
  handleScrollEvent(event) {
    var elHeight = this.messagesScroller.clientHeight;
    var scrollPos= event.target.scrollTop  + elHeight;

    // console.log("--------")
    // console.log( event.target.scrollTop )
    // console.log( event.target.scrollHeight )
    // console.log( event.target.scrollTop  + elHeight)
    // console.log("--------")

    if ( event.target.scrollHeight > scrollPos ) {
      console.log( '-->> scrolled up' )
    }
    var thresh = 50; // threshold pixels
    this.setState({scrolling:(  event.target.scrollHeight > scrollPos + thresh )});

    this.setState({scrollPosition: event.target.scrollHeight - event.target.scrollTop});
    if (this.state.fetchingMessages) {
      return;
    }

    if (event.target.scrollTop <= 0) {
      const topic = this.props.tinode.getTopic(this.state.topic);
      if (topic && topic.isSubscribed() && topic.msgHasMoreMessages()) {
        this.setState({fetchingMessages: true}, () => {
          topic.getMessagesPage(MESSAGES_PAGE)
            .catch((err) => this.props.onError(err.message, 'err'))
            .finally(() => this.setState({fetchingMessages: false}));
          });
      }
    }
  }

  handleGotoLatest() {
    this.messagesScroller.scrollTop = this.messagesScroller.scrollHeight;
  }

  handleDescChange(desc) {
    if (desc.public) {
      this.setState({
        title: desc.public.fn,
        avatar: makeImageDataUrl(desc.public.photo)
      });
    } else {
      this.setState({
        title: '',
        avatar: null
      });
    }

    if (desc.acs) {
      const topic = this.props.tinode.getTopic(this.state.topic);
      const synced = acsFromMeta(desc.acs, topic);
      if (synced) {
        this.setState(synced);
      }
      if (this.props.onTopicAcsUpdate) {
        this.props.onTopicAcsUpdate(desc.acs);
      }
    }
  }

  needsStreamLogin() {
    return isStreamGroupTopic(this.state.topic) &&
      isAnonymousStreamUser(this.props.myUserId);
  }

  canReadMessages() {
    if (!this.state.topic || !this.props.tinode) {
      return false;
    }
    const topic = this.props.tinode.getTopic(this.state.topic);
    const acs = resolveTopicAcs({
      acs: this.props.acs,
      tinode: this.props.tinode,
      topic: this.state.topic
    });

    if (isStreamGroupTopic(this.state.topic)) {
      if (!topic || !topic.isSubscribed()) {
        return true;
      }
      if (topic.messageCount() > 0) {
        return true;
      }
      return acs ? acs.isReader() : !!this.state.isReader;
    }

    if (acs) {
      return acs.isReader();
    }
    return !!this.state.isReader;
  }

  canWriteMessages() {
    if (this.needsStreamLogin()) {
      return false;
    }

    const topic = this.props.tinode && this.state.topic ?
      this.props.tinode.getTopic(this.state.topic) : null;
    const acs = resolveTopicAcs({
      acs: this.props.acs,
      tinode: this.props.tinode,
      topic: this.state.topic
    });

    if (isStreamGroupTopic(this.state.topic)) {
      if (!topic || !topic.isSubscribed()) {
        return false;
      }
      if (acs) {
        return acs.isWriter();
      }
      return true;
    }

    if (acs) {
      return acs.isWriter();
    }
    return !!this.state.isWriter;
  }

  getLatestTopicSeq() {
    const topic = this.props.tinode && this.state.topic ?
      this.props.tinode.getTopic(this.state.topic) : null;
    if (!topic) {
      return 0;
    }
    let max = 0;
    topic.messages((m) => {
      if (m && m.seq > 0 && m.seq > max) {
        max = m.seq;
      }
    });
    return max;
  }

  postReadNotification(seq) {
    if (!this.props.applicationVisible) {
      return;
    }

    let readSeq = seq;
    if (!readSeq || readSeq <= 0) {
      readSeq = this.getLatestTopicSeq();
    }
    if (!readSeq || readSeq <= 0) {
      return;
    }

    if (!this.readNotificationTimer) {
      this.readNotificationTimer = setInterval(() => {
        if (this.readNotificationQueue.length == 0) {
          clearInterval(this.readNotificationTimer);
          this.readNotificationTimer = null;
          return;
        }

        let seq = -1;
        while (this.readNotificationQueue.length > 0) {
          const n = this.readNotificationQueue[0];
          if (n.topicName != this.state.topic) {
            this.readNotificationQueue.shift();
            continue;
          }

          const now = new Date();
          if (n.sendAt <= now) {
            this.readNotificationQueue.shift();
            if (n.seq > 0) {
              seq = Math.max(seq, n.seq);
            }
          } else {
            break;
          }
        }

        if (seq > 0) {
          const topic = this.props.tinode.getTopic(this.state.topic);
          if (topic && topic.isSubscribed()) {
            try {
              topic.noteRead(seq);
            } catch (err) {
              console.warn('noteRead failed', err);
            }
          }
        }
      }, NOTIFICATION_EXEC_INTERVAL);
    }

    const now = new Date();
    this.readNotificationQueue.push({
      topicName: this.state.topic,
      seq: readSeq,
      sendAt: now.setMilliseconds(now.getMilliseconds() + READ_DELAY)
    });
  }

  resolveMessageAuthor(topic, from) {
    const userFrom = from || null;
    let userName = null;
    let userAvatar = null;

    if (from == 'chan') {
      return {
        userFrom: null,
        userName: this.state.title || 'Stream Chat',
        userAvatar: null
      };
    }

    if (from && from == this.props.myUserId) {
      const me = this.props.tinode.getMeTopic();
      if (me && me.public) {
        userName = me.public.fn;
        userAvatar = makeImageDataUrl(me.public.photo);
      }
    } else if (from && topic) {
      const user = topic.userDesc(from);
      if (user && user.public) {
        userName = user.public.fn;
        userAvatar = makeImageDataUrl(user.public.photo);
      }
    }

    if (!userName && from) {
      userName = from;
    }

    return {userFrom, userName, userAvatar};
  }

  // Clear notification queue and timer.
  clearNotificationQueue() {
    this.readNotificationQueue = [];
    if (this.readNotificationTimer) {
      clearInterval(this.readNotificationTimer);
      this.readNotificationTimer = null;
    }
  }

  handleSubsUpdated() {
    if (this.state.topic) {
      const subs = [];
      const topic = this.props.tinode.getTopic(this.state.topic);
      topic.subscribers((sub) => {
        if (sub.online && sub.user != this.props.myUserId) {
          subs.push(sub);
        }
      });
      const newState = {onlineSubs: subs};
      const peer = topic.p2pPeerDesc();
      if (peer) {
        Object.assign(newState, {
          peerMessagingDisabled: isPeerRestricted(peer.acs)
        });
      } else if (this.state.peerMessagingDisabled) {
        Object.assign(newState, {
          peerMessagingDisabled: false
        });
      }
      this.setState(newState);
    }
  }

  // The 'msg' could be false-ish if some message ranges were deleted.
  handleMessageUpdate(msg) {
    const topic = this.props.tinode.getTopic(this.state.topic);
    if (!msg) {
      // msg could be null if one or more messages were deleted.
      // Updating state to force redraw.
      this.setState({latestClearId: topic.maxClearId()});
      return;
    }

    this.setState({messageCount: topic.messageCount()});

    // Scroll to the bottom if the message is added to the end of the message list.
    // TODO: This should be replaced by showing a "scroll to bottom" button.
    if (topic.isNewMessage(msg.seq)) {
      this.setState({scrollPosition: 0});
    }

    // Aknowledge messages except own messages. They are
    // automatically assumed to be read and recived.
    const status = topic.msgStatus(msg, true);
    if (status >= Tinode.MESSAGE_STATUS_SENT && msg.from != this.props.myUserId) {
      this.postReadNotification(msg.seq);
    }
    // This will send "received" notifications right away.
    this.props.onData(msg);
  }

  handleAllMessagesReceived(count) {
    this.setState({fetchingMessages: false});
    if (count > 0) {
      // 0 means "latest".
      this.postReadNotification(0);
    }
  }

  handleInfoReceipt(info) {
    switch (info.what) {
      case 'kp': {
        clearTimeout(this.keyPressTimer);
        var instance = this;
        this.keyPressTimer = setTimeout(function() {
          instance.setState({typingIndicator: false});
        }, KEYPRESS_DELAY + 1000);
        if (!this.state.typingIndicator) {
          this.setState({typingIndicator: true});
        }
        break;
      }
      case 'read':
      case 'recv':
        // Redraw due to changed recv/read status.
        this.forceUpdate();
        break;
      default:
        console.log("Other change in topic: ", info.what);
    }
  }

  handleImagePostview(content) {
    this.setState({ imagePostview: content });
  }

  handleClosePreview() {
    if (this.state.imagePreview && this.state.imagePreview.url) {
      URL.revokeObjectURL(this.state.imagePreview.url);
    }
    this.setState({ imagePostview: null, imagePreview: null, docPreview: null });
  }

  handleFormResponse(action, text, data) {
    if (action == 'pub') {
      this.props.sendMessage(Drafty.attachJSON(Drafty.parse(text), data));
    } else if (action == 'url') {
      const url = new URL(data.ref);
      const params = url.searchParams;
      for (let key in data.resp) {
        if (data.resp.hasOwnProperty(key)) {
          params.set(key, data.resp[key]);
        }
      }
      ['name', 'seq'].map((key) => {
        if (data[key]) {
          params.set(key, data[key]);
        }
      });
      params.set('uid', this.props.myUserId);
      params.set('topic', this.state.topic);
      url.search = params;
      window.open(url, '_blank');
    } else {
      console.log("Unknown action in form", action);
    }
  }

  handleContextClick(e) {
    e.preventDefault();
    e.stopPropagation();
    this.props.showContextMenu({ topicName: this.state.topic, y: e.pageY, x: e.pageX });
  }

  handleShowContextMenuMessage(params, messageSpecificMenuItems) {
    params.topicName = this.state.topic;
    const menuItems = messageSpecificMenuItems || [];
    const topic = this.props.tinode.getTopic(params.topicName);
    if (topic) {
      if (!topic.isChannelType()) {
        menuItems.push('message_delete');
      }
      const acs = topic.getAccessMode();
      if (acs && acs.isDeleter()) {
        menuItems.push('message_delete_hard');
      }
    }
    this.props.showContextMenu(params, menuItems);
  }

  handleNewChatAcceptance(action) {
    this.props.onNewChat(this.state.topic, action);
  }

  handleEnablePeer(e) {
    e.preventDefault();
    this.props.onChangePermissions(this.state.topic, DEFAULT_P2P_ACCESS_MODE, this.state.topic);
  }

  sendKeyPress() {
    const topic = this.props.tinode.getTopic(this.state.topic);
    if (topic.isSubscribed()) {
      topic.noteKeyPress();
    }
  }

  // Send attachment as Drafty message:
  // - if file is too large, upload it and send a s link.
  // - if file is small enough, just send it in-band.
  sendFileAttachment(file) {
    // Server-provided limit reduced for base64 encoding and overhead.
    const maxInbandAttachmentSize = (this.props.tinode.getServerLimit('maxMessageSize',
      MAX_INBAND_ATTACHMENT_SIZE) * 0.75 - 1024) | 0;

    if (file.size > maxInbandAttachmentSize) {
      // Too large to send inband - uploading out of band and sending as a link.
      const uploader = this.props.tinode.getLargeFileHelper();
      if (!uploader) {
        this.props.onError(this.props.intl.formatMessage(messages.cannot_initiate_upload));
        return;
      }
      const uploadCompletionPromise = uploader.upload(file);
      const msg = Drafty.attachFile(null, {
        mime: file.type,
        filename: file.name,
        size: file.size,
        urlPromise: uploadCompletionPromise
      });
      // Pass data and the uploader to the TinodeWeb.
      this.props.sendMessage(msg, uploadCompletionPromise, uploader);
    } else {
      // Small enough to send inband.
      fileToBase64(file,
        (mime, bits, fname) => {
          this.props.sendMessage(Drafty.attachFile(null, {mime: mime, data: bits, filename: fname}));
        },
        this.props.onError
      );
    }
  }

  // sendImageAttachment sends the image bits inband as Drafty message.
  sendImageAttachment(caption, blob) {
    const mime = this.state.imagePreview.type;
    const width = this.state.imagePreview.width;
    const height = this.state.imagePreview.height;
    const fname = this.state.imagePreview.filename;

    // Server-provided limit reduced for base64 encoding and overhead.
    const maxInbandAttachmentSize = (this.props.tinode.getServerLimit('maxMessageSize',
      MAX_INBAND_ATTACHMENT_SIZE) * 0.75 - 1024) | 0;

    if (blob.size > maxInbandAttachmentSize) {
      // Too large to send inband - uploading out of band and sending as a link.
      const uploader = this.props.tinode.getLargeFileHelper();
      if (!uploader) {
        this.props.onError(this.props.intl.formatMessage(messages.cannot_initiate_upload));
        return;
      }
      const uploadCompletionPromise = uploader.upload(blob);

      // Make small preview to show while uploading.
      imageScaled(blob, IMAGE_PREVIEW_DIM, IMAGE_PREVIEW_DIM, -1, false,
        (tinyMine, tinyBlob) => {
          // Convert tiny image into base64 for serialization and previewing.
          blobToBase64(tinyBlob, (blobMime, tinyBits64) => {
            let msg = Drafty.insertImage(null, 0, {
              mime: mime,
              _tempPreview: tinyBits64, // This preview will not be serialized.
              width: width,
              height: height,
              filename: fname,
              size: blob.size,
              urlPromise: uploadCompletionPromise
            });
            if (caption) {
              msg = Drafty.appendLineBreak(msg);
              msg = Drafty.append(msg, Drafty.init(caption));
            }
            // Pass data and the uploader to the TinodeWeb.
            this.props.sendMessage(msg, uploadCompletionPromise, uploader);
          }
        )},
        // Failure
        (err) => {
          this.props.onError(err, 'err');
        }
      );
      return;
    }

    // Upload the image if it's too big to be send inband.
    blobToBase64(blob, (blobMime, bits64) => {
      let msg = Drafty.insertImage(null, 0, {
        mime: blobMime,
        preview: bits64, // Serializable preview
        width: width,
        height: height,
        filename: fname,
        size: blob.size
      });
      if (caption) {
        msg = Drafty.appendLineBreak(msg);
        msg = Drafty.append(msg, Drafty.init(caption));
      }
      this.props.sendMessage(msg);
    });
  }

  handleCancelUpload(seq, uploader) {
    const topic = this.props.tinode.getTopic(this.state.topic);
    const found = topic.findMessage(seq);
    if (found) {
      found._cancelled = true;
    }
    uploader.cancel();
  }

  render() {
    const {formatMessage} = this.props.intl;

    let component;
    if (this.props.connectionFailed) {
      component = (
        <div id="topic-view">
          <div id="topic-caption-panel" className="caption-panel">
            <a href="#" id="hide-message-view" onClick={(e) => {
              e.preventDefault(); this.props.showChat(false);}}>
              <BiArrowFromLeft size={24} />
            </a>
          </div>
          <div id="messages-container">
            <div id="messages-panel">
              <div id="write-only-background">
                <div id="write-only-note">
                  <FormattedMessage id="connection-failed" defaultMessage="Chat not load due connection error."
                                    description="Message shown when webscoket cannot be establish connection." />
                  {' '}
                  <a href="#" onClick={(e) => {
                    e.preventDefault();
                    if (this.props.onRetryConnection) {
                      this.props.onRetryConnection();
                    }
                  }}>Retry</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    } else if (this.props.incognitoBlock) {
      component = (
        <div id="topic-view">
          <div id="topic-caption-panel" className="caption-panel">
            <a href="#" id="hide-message-view" onClick={(e) => {
              e.preventDefault(); this.props.showChat(false);}}>
              <BiArrowFromLeft size={24} />
            </a>
          </div>
          <div id="messages-container">
            <div id="messages-panel">
              <div id="write-only-background">
                <div id="write-only-note">
                  <FormattedMessage id="incognito-block" defaultMessage="Chat not load. Please try to use a non private window."
                                    description="Message shown when IndexedDB does not load." />
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    } else if (!this.state.topic) {
      component = (
        <div id="topic-view">
          <div id="topic-caption-panel" className="caption-panel">
            <a href="#" id="hide-message-view" onClick={(e) => {
              e.preventDefault(); this.props.showChat(false);}}>
              <BiArrowFromLeft size={24} />
            </a>
          </div>
          <LogoView
            serverVersion={this.props.serverVersion}
            serverAddress={this.props.serverAddress} />
        </div>
      );
    } else {
      let component2;
      if (this.state.imagePreview) {
        // Preview image before sending.
        component2 = (
          <ImagePreview
            content={this.state.imagePreview}
            onClose={this.handleClosePreview}
            onSendMessage={this.sendImageAttachment} />
        );
      } else if (this.state.imagePostview) {
        // Expand received image.
        component2 = (
          <ImagePreview
            content={this.state.imagePostview}
            onClose={this.handleClosePreview} />
        );
      } else if (this.state.docPreview) {
        // Preview attachment before sending.
        component2 = (
          <DocPreview
            content={this.state.docPreview}
            onClose={this.handleClosePreview}
            onSendMessage={this.sendFileAttachment} />
        );
      } else {
        const topic = this.props.tinode.getTopic(this.state.topic);
        const isChannel = topic.isChannelType();
        const groupTopic = topic.isGroupType() && !isChannel;
        let messageNodes = [];
        let previousFrom = null;
        let chatBoxClass = null;
        topic.messages((msg, prev, next, i) => {
          let nextFrom = next ? (next.from || null) : 'chan';

          let sequence = 'single';
          let thisFrom = msg.from || 'chan';
          if (thisFrom == previousFrom) {
            if (thisFrom == nextFrom) {
              sequence = 'middle';
            } else {
              sequence = 'last';
            }
          } else if (thisFrom == nextFrom) {
            sequence = 'first';
          }
          previousFrom = thisFrom;

          const isReply = !(thisFrom == this.props.myUserId);
          const deliveryStatus = topic.msgStatus(msg, true);

          const {userFrom, userName, userAvatar} = this.resolveMessageAuthor(topic, thisFrom);
          chatBoxClass='chat-box group';

          messageNodes.push(
            <ChatMessage
              tinode={this.props.tinode}
              content={msg.content}
              deleted={msg.hi}
              mimeType={msg.head ? msg.head.mime : null}
              timestamp={msg.ts}
              response={isReply}
              seq={msg.seq}
              userFrom={userFrom}
              userName={userName}
              userAvatar={userAvatar}
              sequence={sequence}
              received={deliveryStatus}
              uploader={msg._uploader}
              viewportWidth={this.props.viewportWidth}
              showContextMenu={this.state.channel? false : this.handleShowContextMenuMessage}
              onImagePreview={this.handleImagePostview}
              onFormResponse={this.handleFormResponse}
              onError={this.props.onError}
              onCancelUpload={this.handleCancelUpload}
              key={msg.seq} />
          );
        });

        let lastSeen = null;
        if (isChannel) {
          lastSeen = formatMessage(messages.channel);
        } else {
          const cont = this.props.tinode.getMeTopic().getContact(this.state.topic);
          if (cont && Tinode.isP2PTopicName(cont.topic)) {
            if (cont.online) {
              lastSeen = formatMessage(messages.online_now);
            } else if (cont.seen) {
              lastSeen = formatMessage(messages.last_seen) + ": " +
                shortDateFormat(cont.seen.when, this.props.intl.locale);
              // TODO: also handle user agent in c.seen.ua
            }
          }
        }
        const avatar = this.state.avatar || true;
        const online = this.props.online ? 'online' + (this.state.typingIndicator ? ' typing' : '') : 'offline';
        const scrolling = this.state.scrolling;

        //ref={(e) => this.handleScrollReference(e)}
        //onScroll={this.handleScrollEvent}

        component2 = (
          <>
            <div id="topic-caption-panel" className="caption-panel">
              <a href="#" id="hide-message-view" onClick={(e) => {
                e.preventDefault(); this.props.showChat(false);}}>
                <BiArrowFromLeft size={24} />
              </a>
              <div id="topic-title-group">
                <div id="topic-title" className="panel-title">Stream Chat</div>
              </div>
            </div>
            <LoadSpinner show={this.state.fetchingMessages} />
            <div id="messages-container">
              {scrolling ? <div className='chat-pause-wrapper'>
                  <div onClick={(e) => this.handleGotoLatest(e)} className='chat-pause-pop'>Go to latest</div>
                </div>
              : null }
              <div id="messages-panel"  ref={this.handleScrollReference}> 
                <ul id="scroller" className={chatBoxClass} ref={this.scrollerElement}>
                  {messageNodes}
                </ul>
              </div>
              {!this.canReadMessages() ?
              <div id="write-only-background">
                <div id="write-only-note">
                  <FormattedMessage id="messages_not_readable" defaultMessage="no access to messages"
                    description="Message shown in topic without the read access" />
                </div>
              </div>
              : null }
            </div>
            {this.state.peerMessagingDisabled && !this.state.unconfirmed ?
              <div id="peer-messaging-disabled-note">
                <i className="material-icons secondary">block</i> <FormattedMessage
                  id="peers_messaging_disabled" defaultMessage="Peer's messaging is disabled."
                  description="Shown when the p2p peer's messaging is disabled" /> <a href="#"
                    onClick={this.handleEnablePeer}><FormattedMessage id="enable_peers_messaging"
                    defaultMessage="Enable" description="Call to action to enable peer's messaging" /></a>.
              </div> : null}
            {this.state.unconfirmed ?
              <Invitation onAction={this.handleNewChatAcceptance} />
              :
              <SendMessage
                disabled={!this.canWriteMessages()}
                promptStreamLogin={this.needsStreamLogin()}
                onSendMessage={this.props.sendMessage}
                showStreamLogin={this.props.showStreamLogin}
                onKeyPress={this.sendKeyPress}
                onError={this.props.onError} />}
          </>
        );
      }

      component = <div id="topic-view">{component2}</div>
    }
    return component;
  }
};

export default injectIntl(MessagesView);
